import type { StateCreator } from 'zustand';
import type { ContentFile } from '@/types';

export interface FilesSlice {
  contentFiles: ContentFile[];
  setContentFiles: (files: ContentFile[]) => void;
  prependContentFile: (file: ContentFile) => void;
  addContentFile: (file: ContentFile) => void;
  updateContentFile: (params: {
    uuid: string;
    updates: Partial<ContentFile>;
  }) => void;
  removeContentFiles: (uuids: string[]) => void;
  isFilesLoading: boolean;
  setIsFilesLoading: (loading: boolean) => void;
  pendingLightboxFileId: string | null;
  setPendingLightboxFileId: (fileId: string | null) => void;
  isResultMode: boolean;
  setIsResultMode: (value: boolean) => void;
}

export const filesInitialState: Pick<
  FilesSlice,
  'contentFiles' | 'isFilesLoading' | 'pendingLightboxFileId' | 'isResultMode'
> = {
  contentFiles: [],
  isFilesLoading: false,
  pendingLightboxFileId: null,
  isResultMode: false,
};

export const createFilesSlice: StateCreator<FilesSlice, [], [], FilesSlice> = (
  set
) => ({
  ...filesInitialState,

  setContentFiles: (contentFiles) => set({ contentFiles }),

  prependContentFile: (file) =>
    set((state) => ({
      contentFiles: [file, ...state.contentFiles],
    })),

  addContentFile: (file) =>
    set((state) => {
      const exists = state.contentFiles.some((f) => f.uuid === file.uuid);
      if (exists) {
        return { contentFiles: state.contentFiles };
      }
      return { contentFiles: [file, ...state.contentFiles] };
    }),

  updateContentFile: ({ uuid, updates }) =>
    set((state) => ({
      contentFiles: state.contentFiles.map((file) =>
        file.uuid === uuid ? { ...file, ...updates } : file
      ),
    })),

  removeContentFiles: (uuids) =>
    set((state) => {
      const uuidSet = new Set(uuids);
      return {
        contentFiles: state.contentFiles.filter((f) => !uuidSet.has(f.uuid)),
      };
    }),

  setIsFilesLoading: (isFilesLoading) => set({ isFilesLoading }),

  setPendingLightboxFileId: (pendingLightboxFileId) =>
    set({ pendingLightboxFileId }),

  setIsResultMode: (isResultMode) => set({ isResultMode }),
});
